import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { buildLoginRequest, getForgotPasswordPolicy } from './core/config/msal-config';
import { GoogleAnalyticsService } from './shared/services/google-analytics.service';
import { NewVersionCheckerService } from './shared/services/new-version-checker.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false // NOSONAR - required: component is declared in AppModule (NgModule-based architecture)
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly _newVersionCheckerService = inject(NewVersionCheckerService);
  private readonly _router = inject(Router);
  private readonly _msalService = inject(MsalService);
  
  private isFirstNavigation = true;
  private readonly _destroying$ = new Subject<void>();
  private hasRedirectedToError = false;

  ngOnInit(): void {
    this._googleAnalyticsService.initialize();
    this._newVersionCheckerService.init();
    this.checkForB2CErrors();
    this.initializeMsal();

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (this.isFirstNavigation) {
          this.isFirstNavigation = false;
          document.documentElement.classList.add('no-view-transition');
          const timeout = setTimeout(() => {
            document.documentElement.classList.remove('no-view-transition');
            clearTimeout(timeout);
          }, 100);
        }
      });
  }

  private initializeMsal(): void {
    this._msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult | null) => {
        if (result) {
      
          const claims = result.idTokenClaims as Record<string, string> | undefined;
          const issuedPolicy = claims?.['tfp'] ?? claims?.['acr'];
          const forgotPasswordPolicy = getForgotPasswordPolicy();

          if (issuedPolicy?.toLowerCase() === forgotPasswordPolicy.toLowerCase()) {
            const loginRequest = buildLoginRequest();
            this._msalService.loginRedirect({ ...loginRequest, prompt: 'login' });
            return;
          }

          this._msalService.instance.setActiveAccount(result.account);
        }
      },
      error: (error) => {
        if (!this.hasRedirectedToError) {
          this.hasRedirectedToError = true;
          
          const errorCode = error.errorCode || 'unknown_error';
          const errorMessage: string = error.errorMessage ?? error.message ?? '';

          const isCancelledForgotPassword = errorCode === 'AADB2C90091' || errorMessage.includes('AADB2C90091');

          if (isCancelledForgotPassword) {
            this._msalService.loginRedirect();
            return;
          }

          void this._router.navigate(['/error/error-inicio-sesion'], {
            queryParams: {
              error: errorCode,
              error_description: error.errorMessage || 'Error desconocido durante la autenticación'
            }
          });
        }
      }
    });

  }

  private checkForB2CErrors(): void {
    if (this.hasRedirectedToError) return;

    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    const error = urlParams.get('error') || hashParams.get('error');
    const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');

    if (!error) return;

    const isForgotPassword = errorDescription?.includes('AADB2C90118');
    const isCancelledForgotPassword = errorDescription?.includes('AADB2C90091');

    if (isForgotPassword) return;

    this.hasRedirectedToError = true;

    if (isCancelledForgotPassword) {
      void this._router.navigate(['/'], { replaceUrl: true });
      return;
    }

    void this._router.navigate(['/error/error-inicio-sesion'], {
      queryParams: { error, error_description: errorDescription },
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
